/**
 * ImageUploader Component
 * Allows customers to upload damage photos during booking
 */

import { useState, useRef, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { IMAGE_UPLOAD_CONSTRAINTS } from '@leal-crm/shared';
import { Upload, X, ImagePlus, AlertCircle, Loader2 } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.lealchapaypintura.com';

interface UploadedImage {
  id: string;
  previewUrl: string;
  localPreview?: string;
  uploading?: boolean;
  error?: string;
}

interface ImageUploaderProps {
  trackingToken: string;
  images: UploadedImage[];
  onImagesChange: (images: UploadedImage[]) => void;
  maxImages?: number;
  disabled?: boolean;
}

const ACCEPTED_TYPES = IMAGE_UPLOAD_CONSTRAINTS.acceptedTypes.join(',');
const MAX_SIZE_MB = IMAGE_UPLOAD_CONSTRAINTS.maxFileSize / (1024 * 1024);

export function ImageUploader({
  trackingToken,
  images,
  onImagesChange,
  maxImages = Number.POSITIVE_INFINITY,
  disabled = false,
}: ImageUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasMaxLimit = Number.isFinite(maxImages);
  const canAddMore = hasMaxLimit ? images.length < maxImages : true;

  const validateFile = useCallback((file: File): string | null => {
    if (!IMAGE_UPLOAD_CONSTRAINTS.acceptedTypes.includes(file.type as (typeof IMAGE_UPLOAD_CONSTRAINTS.acceptedTypes)[number])) {
      return `Tipo de archivo no soportado. Formatos aceptados: JPEG, PNG, WebP, HEIC`;
    }
    if (file.size > IMAGE_UPLOAD_CONSTRAINTS.maxFileSize) {
      return `El archivo es demasiado grande. Maximo: ${MAX_SIZE_MB}MB`;
    }
    return null;
  }, []);

  const uploadFile = useCallback(
    async (file: File): Promise<UploadedImage | null> => {
      const error = validateFile(file);
      if (error) {
        setUploadError(error);
        return null;
      }

      // Create local preview
      const localPreview = URL.createObjectURL(file);
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      // Add placeholder immediately
      const placeholder: UploadedImage = {
        id: tempId,
        previewUrl: '',
        localPreview,
        uploading: true,
      };

      onImagesChange([...images, placeholder]);

      try {
        const formData = new FormData();
        formData.append('image', file);

        const response = await fetch(`${API_BASE}/api/public/booking/upload-image`, {
          method: 'POST',
          headers: {
            'X-Tracking-Token': trackingToken,
          },
          body: formData,
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Error al subir imagen');
        }

        const data = await response.json();

        // Update with real data
        const uploaded: UploadedImage = {
          id: data.data.imageId,
          previewUrl: data.data.previewUrl,
          localPreview,
        };

        // Replace placeholder with actual image
        onImagesChange(
          images
            .filter((img) => img.id !== tempId)
            .concat(uploaded)
        );

        setUploadError(null);
        return uploaded;
      } catch (err) {
        // Update placeholder to show error
        const errorMessage = err instanceof Error ? err.message : 'Error al subir imagen';
        onImagesChange(
          images.map((img) =>
            img.id === tempId
              ? { ...img, uploading: false, error: errorMessage }
              : img
          )
        );
        return null;
      }
    },
    [images, onImagesChange, trackingToken, validateFile]
  );

  const handleFileSelect = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;

      setUploadError(null);

      const filesToUpload = hasMaxLimit
        ? Array.from(files).slice(0, Math.max(0, maxImages - images.length))
        : Array.from(files);

      for (const file of filesToUpload) {
        await uploadFile(file);
      }
    },
    [images.length, maxImages, hasMaxLimit, uploadFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      if (disabled || !canAddMore) return;

      handleFileSelect(e.dataTransfer.files);
    },
    [disabled, canAddMore, handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleRemove = useCallback(
    async (imageId: string) => {
      const image = images.find((img) => img.id === imageId);
      if (!image) return;

      // Revoke local preview URL
      if (image.localPreview) {
        URL.revokeObjectURL(image.localPreview);
      }

      // If it's a temp/uploading image, just remove locally
      if (imageId.startsWith('temp-') || image.uploading) {
        onImagesChange(images.filter((img) => img.id !== imageId));
        return;
      }

      // Delete from server
      try {
        const response = await fetch(`${API_BASE}/api/public/booking/delete-image/${imageId}`, {
          method: 'DELETE',
          headers: {
            'X-Tracking-Token': trackingToken,
          },
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Error al eliminar imagen');
        }

        onImagesChange(images.filter((img) => img.id !== imageId));
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : 'Error al eliminar imagen');
      }
    },
    [images, onImagesChange, trackingToken]
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">
          Fotos del vehiculo (opcional)
        </label>
        <span className="text-xs text-gray-500">
          {hasMaxLimit ? `${images.length}/${maxImages} imagenes` : `${images.length} imagenes`}
        </span>
      </div>

      {/* Upload area */}
      {canAddMore && !disabled && (
        <Card
          className={`border-2 border-dashed transition-colors cursor-pointer ${
            isDragging
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <CardContent className="flex flex-col items-center justify-center py-8">
            <div className="rounded-full bg-gray-100 p-3 mb-3">
              <ImagePlus className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-sm text-gray-600 text-center">
              Arrastra imagenes aqui o{' '}
              <span className="text-blue-600 font-medium">selecciona archivos</span>
            </p>
            <p className="text-xs text-gray-400 mt-1">
              JPEG, PNG, WebP o HEIC. Max {MAX_SIZE_MB}MB
            </p>
          </CardContent>
        </Card>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        multiple
        className="hidden"
        onChange={(e) => handleFileSelect(e.target.files)}
        disabled={disabled || !canAddMore}
      />

      {/* Error message */}
      {uploadError && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-md p-3">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{uploadError}</span>
        </div>
      )}

      {/* Image previews */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {images.map((image) => (
            <div
              key={image.id}
              className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group"
            >
              {image.localPreview || image.previewUrl ? (
                <img
                  src={image.localPreview || image.previewUrl}
                  alt="Vista previa"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Upload className="h-8 w-8 text-gray-400" />
                </div>
              )}

              {/* Loading overlay */}
              {image.uploading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 text-white animate-spin" />
                </div>
              )}

              {/* Error overlay */}
              {image.error && (
                <div className="absolute inset-0 bg-red-500/80 flex items-center justify-center p-2">
                  <p className="text-xs text-white text-center">{image.error}</p>
                </div>
              )}

              {/* Remove button */}
              {!disabled && !image.uploading && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(image.id);
                  }}
                  className="absolute top-1 right-1 p-1 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Max images message */}
      {!canAddMore && (
        <p className="text-xs text-gray-500 text-center">
          {hasMaxLimit ? `Has alcanzado el maximo de ${maxImages} imagenes` : 'Has alcanzado el maximo de imagenes'}
        </p>
      )}
    </div>
  );
}
